﻿import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import useFilterStore from "@/stores/useFilterStore";
import useListController from "@/hooks/list/useListController";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useEstatesParentsList, useEstatesParentsLookups, useDeleteEstatesParent } from "@/hooks/maintainers/estates-parents/useEstatesParents";
import { SCREEN_ID_ESTATE_PARENT } from "@/constants/screenIds";

import { base_table } from "./estates-parents.constants";

import EstatesParentsForm from "./Form/EstatesParentsForm";
import useEstatesParentsColumns from "./hooks/useEstatesParentsColumns";

const EstatesParents = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { estate_id, estate_parent_id, search } = filter || {};

	const { canShow, canEdit, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_ESTATE_PARENT);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const { tableState, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: [],
		baseTable: base_table,
	});

	const listFilters = useMemo(
		() => ({
			estate_id,
			estate_parent_id,
			search,
			...tableState,
		}),
		[estate_id, estate_parent_id, search, tableState],
	);

	const { data: lookups, isLoading: isLoadingLookups } = useEstatesParentsLookups({
		enabled: canLoadScreenData,
	});

	const { data: listResponse, isLoading: isLoadingList } = useEstatesParentsList(listFilters, {
		enabled: canLoadScreenData && Boolean(lookups),
	});

	const { mutate: deleteEstatesParent, isLoading: isLoadingDelete } = useDeleteEstatesParent();

	const estates = useMemo(() => lookups?.data.estates ?? [], [lookups]);
	const regions = useMemo(() => lookups?.data.regions ?? [], [lookups]);

	const [showDelete, setShowDelete] = useState(false);
	const [selected, setSelected] = useState(null);

	const handleDelete = () => {
		setShowDelete(false);

		deleteEstatesParent(selected, {
			onSuccess: () => toast.success("Campo eliminado correctamente"),
			onError: (error) => toast.error(error?.response?.data?.error || "Error al eliminar el campo"),
		});
	};

	const handleOpenDelete = useCallback((id) => {
		setSelected(id);
		setShowDelete(true);
	}, []);

	const columns = useEstatesParentsColumns({
		tableState,
		estates,
		regions,
		canEdit,
		onDelete: handleOpenDelete,
	});

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion") },
				{ label: "Campos", onClick: () => navigate("/configuracion/grupos_campos") },
			]}
			subtitle="Gestiona los campos y las bodegas asociadas"
			showCreate={canEdit}
			createLabel="Nuevo Campo"
			onCreate={() => navigate("/configuracion/grupos_campos/crear")}
			actionButtons={[]}
			isLoading={isLoadingList || isLoadingLookups || isLoadingDelete}
			data={listResponse?.data || []}
			totalItems={listResponse?.totalRecords || 0}
			totalPages={listResponse?.totalPages || 0}
			toItem={listResponse?.data?.length || 0}
			fromItem={listResponse?.page === 1 ? 1 : (listResponse?.page - 1) * tableState.per_page + 1}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
			compactHeaders={true}
		>
			<EstatesParentsForm filters={listFilters} />

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation="eliminar campo"
			/>
		</ListScaffold>
	);
};

export default EstatesParents;
